/**
 * Wire types para Tenant Lifecycle — W3.3 Sprint Frontend Desktop.
 *
 * Refletem TenantLifecycleSnapshot + TenantLifecycleHistoryEvent do Engine
 * sem importar tipos Prisma diretamente (mantém atendimento desacoplado).
 * ADR-111 7 estados canônicos.
 */

/** 7 estados canônicos ADR-111 + legacy inactive. */
export type TenantStatus =
  | 'onboarding'
  | 'active'
  | 'payment_overdue'
  | 'suspended'
  | 'churning'
  | 'churned'
  | 'archived'
  | 'inactive';

/** Tipos de pacote (tier). */
export type PackageType = 'solo' | 'team' | 'professional' | 'regulated';

/** Snapshot de lifecycle de um Tenant — serializado para leitura pública. */
export interface TenantLifecycleSnapshotWire {
  id: string;
  name: string;
  slug: string;
  packageType: PackageType;
  status: TenantStatus;
  version: number;
  onboardingStartedAt: string | null;
  onboardingCompletedAt: string | null;
  activatedAt: string | null;
  paymentOverdueDetectedAt: string | null;
  paymentOverdueGracePeriodEndsAt: string | null;
  suspendedAt: string | null;
  suspendedSource: string | null;
  churningStartedAt: string | null;
  churningCancellableUntil: string | null;
  churningProgressJson: Record<string, unknown> | null;
  churningWorkerLastHeartbeat: string | null;
  churnedAt: string | null;
  archivedAt: string | null;
  churningCancelledAt: string | null;
  createdAt: string;
  updatedAt: string | null;
}

/** Evento histórico de lifecycle — serializado. */
export interface TenantLifecycleHistoryEventWire {
  id: string;
  fromStatus: TenantStatus;
  toStatus: TenantStatus;
  transitionedAt: string;
  actorUserId: string | null;
  actorSystem: string | null;
  reason: string | null;
}

/** Response de GET /v1/tenants/:id/lifecycle. */
export interface TenantLifecycleDetailResponse {
  tenant: TenantLifecycleSnapshotWire;
  history: TenantLifecycleHistoryEventWire[];
}

/** Response de GET /v1/tenants/lifecycle/dashboard. */
export interface TenantLifecycleDashboardResponse {
  items: TenantLifecycleSnapshotWire[];
  nextCursor: string | null;
  totals: Partial<Record<TenantStatus, number>>;
}

/** Params para o dashboard. */
export interface TenantLifecycleDashboardParams {
  status?: TenantStatus;
  packageType?: PackageType;
  cursor?: string;
  limit?: number;
}

/** Body para POST /v1/tenants/:id/lifecycle/transition. */
export interface TransitionTenantStatusBody {
  toStatus: TenantStatus;
  reason?: string | null;
}

/** Body para POST /v1/tenants/:id/lifecycle/churning/initiate. */
export interface InitiateChurningBody {
  reason: string;
  anonymizationLevel?: 'mask' | 'pseudonymize' | 'hash_irreversible';
  cancellablePeriodDays?: number;
}

/** Body para POST /v1/tenants/:id/lifecycle/churning/cancel. */
export interface CancelChurningBody {
  reason: string;
}

/** Rótulos PT-BR por status (display). */
export const STATUS_LABELS: Record<TenantStatus, string> = {
  onboarding: 'Onboarding',
  active: 'Ativo',
  payment_overdue: 'Pagamento Atrasado',
  suspended: 'Suspenso',
  churning: 'Em Churning',
  churned: 'Churned',
  archived: 'Arquivado',
  inactive: 'Inativo',
};

/** Cores semânticas por status. */
export const STATUS_COLORS: Record<TenantStatus, { bg: string; fg: string; border: string }> = {
  onboarding: { bg: '#eff6ff', fg: '#1d4ed8', border: '#bfdbfe' },
  active: { bg: '#f0fdf4', fg: '#15803d', border: '#bbf7d0' },
  payment_overdue: { bg: '#fffbeb', fg: '#b45309', border: '#fde68a' },
  suspended: { bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca' },
  churning: { bg: '#fdf4ff', fg: '#7e22ce', border: '#e9d5ff' },
  churned: { bg: '#f3f4f6', fg: '#374151', border: '#d1d5db' },
  archived: { bg: '#f9fafb', fg: '#6b7280', border: '#e5e7eb' },
  inactive: { bg: '#f9fafb', fg: '#9ca3af', border: '#e5e7eb' },
};

/** Estados terminais (sem transições possíveis). */
export const TERMINAL_STATUSES: TenantStatus[] = ['churned', 'archived'];

/** Estados que permitem transição manual (não-terminal, não churning). */
export const MANUALLY_TRANSITIONABLE: TenantStatus[] = [
  'onboarding',
  'active',
  'payment_overdue',
  'suspended',
];
